<script>
import FormModal from "@/components/form-modal.vue";
import GenericModal from "@/components/g-modal.vue";
import Table from "@/components/master-detail.vue";
import { FieldTypeEnum } from "@/core/enums/field-types";
import { mapGetters } from "vuex";
import { isNil } from "lodash";
import { ImpairmentStatus } from "./impairment.types";
import impairmentsService from "@/api/ativacaoped/impairments.service";
import { createServiceResource } from "@/api/ativacaoped/resource-adapter";

export default {
  components: { FormModal, GenericModal, Table },
  data() {
    return {
      addForm: {
        opened: false,
        value: { businessPlanId: null }
      },
      createVersionModal: {
        opened: false,
        value: { businessPlanId: null }
      },
      opts: {
        businessPlans: []
      }
    };
  },
  computed: {
    ...mapGetters(["clientId"]),
    actionBarButtons() {
      return [
        {
          text: "Adicionar",
          icon: "mdi-plus-box-outline",
          action: async () => {
            await this.getBusinessPlans();
            this.addForm.value.businessPlanId = null;
            this.addForm.opened = true;
          }
        }
      ];
    },
    cols() {
      return [
        {
          key: "tituloBP",
          name: "Título do BP",
          type: FieldTypeEnum.TEXT
        },
        {
          key: "status_label",
          name: "Status",
          type: FieldTypeEnum.TEXT,
        },
        {
          key: "conta_contabil_nome",
          name: "Conta Contábil",
          type: FieldTypeEnum.TEXT,
        },
        {
          key: "data_avaliacao",
          name: "Ref. data Impairment",
          type: FieldTypeEnum.MONTH,
          align: 1
        },
        {
          key: "valor_contabil",
          name: "Valor Contábil",
          type: FieldTypeEnum.MONEY,
          align: 1
        },
        {
          key: "armotizacao_acumulada_anteriores",
          name: "Valor Amortização",
          type: FieldTypeEnum.MONEY,
          align: 1
        },
        {
          key: "valor_recuperavel",
          name: "Valor Recuperável",
          type: FieldTypeEnum.MONEY,
          align: 1
        },
        {
          key: "versao_avaliacao",
          name: "Versão Impairment",
          type: FieldTypeEnum.TEXT,
          align: 1
        }
      ];
    },
    fields() {
      return [
        {
          key: "businessPlanId",
          name: "Título do BP",
          type: FieldTypeEnum.AUTOCOMPLETE,
          rel: { to: "businessPlans", key: "id", name: "label" }
        }
      ];
    },
    /**
     * Resource service-backed consumido pelo `MasterDetail` (`get` na grid e
     * `delete`). Criar/editar passa pelos modais da tela e o menu de contexto
     * da grade está desabilitado (`disableContextMenu`).
     * Substitui o `:resourceUrl`, que usava o `apiResource` inline do mixin.
     *
     * @returns {Object} Resource no contrato do `apiResource`
     */
    customResource() {
      // Ver nota em `divisoes.vue`: ler `clientId` aqui mantém o reload da grid
      // pelo watcher de `customResource` do `MasterDetail`.
      const clientId = this.clientId;

      return createServiceResource({
        notify: this.notify,
        get: ({ query }) => impairmentsService.getImpairments(clientId, query),
        remove: id => impairmentsService.deleteImpairment(clientId, id)
      });
    }
  },
  created() {
    if (this.clientId === 0) {
      this.goToHome();
    }
  },
  watch: {
    clientId(value) {
      if (value === 0) {
        this.goToHome();
      }
    }
  },
  methods: {
    onLoadedRows(rows) {
      if (!Array.isArray(rows)) {
        return;
      }

      rows.forEach(item => {
        item.tituloBP = `${item.nome_iniciativa} - ${
          item.versao_iniciativa || "Versão desconhecida"
        }`;
      });
    },
    contextmenu(row, event, _, col) {
      setTimeout(() => {
        const menu = [
          {
            name: "Teste de Impairment",
            disabled: ![
              ImpairmentStatus.APROVADO,
              ImpairmentStatus.REALIZADO,
              ImpairmentStatus.REPROVADO
            ].includes(row.status),
            cb: ({ id }) => {
              this.$router.push({
                name: "ativacaoped-impairment-test",
                params: { id }
              });
            }
          },
          {
            name: "Nova versão",
            cb: ({ id }) => {
              this.createVersionModal.value.businessPlanId = id;
              this.createVersionModal.opened = true;
            }
          },
          {
            name: "Editar",
            cb: ({ id }) => {
              this.$router.push({
                name: "ativacaoped-impairment-form",
                params: { id }
              });
            }
          }
        ];

        this.showContext({
          left: event.clientX,
          top: event.clientY,
          val: row,
          col,
          menu
        });
      }, 100);
    },
    async doAddSave(data, close) {
      try {
        if (isNil(data.businessPlanId)) {
          this.$notify({
            group: "geral",
            duration: 5000,
            text: "Business Plan não foi identificado. Tente novamente.",
            title: "Impairment não iniciado",
            type: "warn"
          });
          return;
        }

        await impairmentsService.postImpairment(this.clientId, {
          business_plan_id: data.businessPlanId
        });
        await this.$refs.table.doLoad();

        if (close) {
          close();
        }
      } catch (error) {
        this.notify(error);
      }
    },
    async doCreateNewVersion() {
      try {
        await impairmentsService.postCriarNovaVersao(
          this.clientId,
          this.createVersionModal.value.businessPlanId
        );
        this.$notify({
          group: "geral",
          duration: 5000,
          text: "Nova versão criada com sucesso",
          title: "Nova versão criada",
          type: "success"
        });
        await this.$refs.table.doLoad();
        this.createVersionModal.opened = false;
      } catch (error) {
        this.notify(error);
      }
    },
    async getBusinessPlans() {
      try {
        const response = await impairmentsService.getSelecaoImpairments(
          this.clientId
        );
        const businessPlans = Array.isArray(response) ? response : [];
        this.opts.businessPlans = businessPlans
          .map(bp => ({
            ...bp,
            label: `${bp.nome} - ${bp.versao_label || "Versão desconhecida"}`
          }))
          .sort(({ label: a = "" }, { label: b = "" }) => a.localeCompare(b));
      } catch (error) {
        this.opts.businessPlans = [];
        this.notify(error);
      }
    },
    goToHome() {
      this.notify({
        error:
          "Não é possível acessar o módulo de Ativação P&D sem uma empresa selecionada."
      });
      this.$router.push({ name: "homepage" });
    },
  }
};
</script>

<template>
  <Table
    ref="table"
    :actionBarButtons="actionBarButtons"
    :cols="cols"
    :customResource="customResource"
    @loadedRows="onLoadedRows"
    disableContextMenu
    :hasExportCSV="false"
    :hasFilter="false"
    :hasNewButton="false"
    :opts="opts"
    serverPagination
    @contextmenu="contextmenu"
  >
    <FormModal
      :cols="fields"
      :opened.sync="addForm.opened"
      :opts="opts"
      title="Adicionar Impairment"
      titleIcon="mdi-plus-box-outline"
      :value.sync="addForm.value"
      :maxWidth="600"
      @save="doAddSave"
    ></FormModal>

    <GenericModal
      hideCloseIcon
      :opened.sync="createVersionModal.opened"
      title="Confirmar nova versão"
    >
      <p class="pt-3">
        Tem certeza que deseja criar uma nova versão deste impairment?
      </p>

      <template #buttons="{ close }">
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          dark
          depressed
          @click="close(null)"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          dark
          depressed
          @click="doCreateNewVersion()"
        >
          Confirmar
        </v-btn>
      </template>
    </GenericModal>
  </Table>
</template>
